<div class="px-6 py-4 border-b border-gray-100 bg-white flex flex-row justify-between items-center gap-4">
  <div class="flex items-center gap-3">
    <span class="h-10 w-10 rounded-xl bg-neutral-900 text-amber-400 flex items-center justify-center"><mat-icon>qr_code_2</mat-icon></span>
    <div>
      <h1 class="text-xl font-bold text-gray-900 leading-tight">Print barcode labels</h1>
      <p class="text-xs text-gray-500">{{ type === 'regular' ? 'Same layout as Odoo\'s product labels - Code 128' : 'Crate label with the crate and slab / m² barcodes' }}</p>
    </div>
  </div>
  <button type="button" (click)="dialogRef.close()" class="h-9 w-9 rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-800 flex items-center justify-center" aria-label="Close">
    <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">close</mat-icon>
  </button>
</div>

<mat-dialog-content class="mat-typography !px-6 !py-5 !max-h-[68vh]">
  <!-- Which label -->
  <div class="grid grid-cols-2 gap-2 mb-5">
    @for (t of [
      { key: 'regular', title: 'Regular Barcode', hint: 'Small product label - SKU and barcode', icon: 'qr_code_2' },
      { key: 'variant', title: 'Variant Barcode', hint: 'Crate label - crate and slab / m² barcodes', icon: 'inventory_2' }
    ]; track t.key) {
    <button type="button" class="flex items-center gap-3 px-4 py-3 rounded-2xl border-2 text-left transition-all"
      [ngClass]="type === t.key ? 'border-neutral-900 bg-neutral-900 text-white' : 'border-gray-200 hover:border-amber-300 hover:bg-amber-50'"
      (click)="setType($any(t.key))">
      <mat-icon [ngClass]="type === t.key ? 'text-amber-400' : 'text-gray-400'">{{ t.icon }}</mat-icon>
      <span><span class="block text-sm font-bold">{{ t.title }}</span><span class="block text-xs" [ngClass]="type === t.key ? 'text-white/60' : 'text-gray-500'">{{ t.hint }}</span></span>
    </button>
    }
  </div>

  <div class="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_300px] gap-6">

    <!-- Products and copies -->
    <div class="min-w-0">
      <div class="flex items-center justify-between mb-2">
        <span class="text-[11px] font-bold uppercase tracking-wider text-gray-500">{{ rows.length }} product{{ rows.length === 1 ? '' : 's' }}</span>
        <span class="flex items-center gap-1 text-xs">
          <span class="text-gray-400 mr-1">Copies of each:</span>
          @for (n of [1, 2, 5, 10]; track n) {
          <button type="button" class="px-2 py-0.5 rounded-md font-semibold text-gray-600 hover:bg-amber-50 hover:text-amber-800" (click)="setAllCopies(n)">{{ n }}</button>
          }
        </span>
      </div>
      <div class="rounded-xl border border-gray-200 divide-y divide-gray-100 max-h-[44vh] overflow-y-auto">
        @for (r of rows; track r.item.productVariantId; let i = $index) {
        <div class="flex items-center gap-3 px-3 py-2">
          <div class="min-w-0 flex-1">
            <div class="font-mono text-xs font-bold text-gray-900 truncate" [title]="r.item.sku">{{ r.item.sku }}</div>
            <div class="text-[11px] text-gray-400 truncate">{{ r.item.barcode }} &middot; {{ r.item.productName }}</div>
          </div>
          <input type="number" min="0" step="1" class="w-16 px-2 py-1 rounded-lg border border-gray-200 text-sm text-right font-semibold outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
            [(ngModel)]="r.copies" [attr.aria-label]="'Copies of ' + r.item.sku" />
          <button type="button" class="h-7 w-7 rounded-full text-gray-300 hover:bg-red-50 hover:text-red-500 flex items-center justify-center" (click)="remove(i)" matTooltip="Leave out" aria-label="Leave out">
            <mat-icon class="!text-[16px] !h-4 !w-4">close</mat-icon>
          </button>
        </div>
        } @empty {
        <div class="px-3 py-6 text-center text-sm text-gray-400">None of the selected products has a barcode</div>
        }
      </div>
      @if (type === 'variant' && unlinkedCount) {
      <div class="mt-2 flex items-start gap-1.5 text-xs text-amber-800">
        <mat-icon class="!text-[14px] !h-3.5 !w-3.5 flex-shrink-0 mt-px">link_off</mat-icon>
        <span>{{ unlinkedCount }} with no slab / m² item linked - their second barcode prints empty. Set Extreme Products on the item to fill it.</span>
      </div>
      }
      @if (missing.length) {
      <div class="mt-2 flex items-start gap-1.5 text-xs text-amber-800">
        <mat-icon class="!text-[14px] !h-3.5 !w-3.5 flex-shrink-0 mt-px">warning_amber</mat-icon>
        <span>{{ missing.length }} without a barcode left out: {{ missingList }}</span>
      </div>
      }
    </div>

    <!-- Settings + preview -->
    <div class="flex flex-col gap-4">
      @if (type === 'variant') {
      <div>
        <div class="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">Paper</div>
        <div class="flex flex-col gap-1.5">
          @for (s of variantSizes; track s.key) {
          <button type="button" class="flex items-center justify-between px-3 py-2 rounded-xl border text-sm font-semibold transition-colors"
            [ngClass]="variantSize.key === s.key ? 'border-neutral-900 bg-neutral-900 text-white' : 'border-gray-200 text-gray-700 hover:border-amber-300 hover:bg-amber-50'" (click)="variantSize = s">
            {{ s.label }}<span class="text-[10px] font-medium opacity-70">{{ s.w }} × {{ s.h }} mm</span>
          </button>
          }
        </div>
        <p class="mt-1.5 text-[11px] text-gray-400">One label per sheet.</p>
      </div>
      } @else {
      <div>
        <div class="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">Label size</div>
        <div class="flex flex-col gap-1.5">
          @for (s of sizes; track s.key) {
          <button type="button" class="flex items-center justify-between px-3 py-2 rounded-xl border text-sm font-semibold transition-colors"
            [ngClass]="size.key === s.key ? 'border-neutral-900 bg-neutral-900 text-white' : 'border-gray-200 text-gray-700 hover:border-amber-300 hover:bg-amber-50'" (click)="size = s">
            {{ s.label }}
            @if (s.key === '50x30') { <span class="text-[10px] font-medium opacity-70">like Odoo</span> }
          </button>
          }
        </div>
      </div>
      <div>
        <div class="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">Print on</div>
        <div class="flex bg-gray-100 rounded-xl p-1 gap-1">
          <button type="button" class="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-semibold transition-all"
            [ngClass]="layout === 'roll' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'" (click)="layout = 'roll'">
            <mat-icon class="!text-[16px] !h-4 !w-4">receipt</mat-icon>Label printer
          </button>
          <button type="button" class="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-semibold transition-all"
            [ngClass]="layout === 'a4' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'" (click)="layout = 'a4'">
            <mat-icon class="!text-[16px] !h-4 !w-4">grid_view</mat-icon>A4 sheet
          </button>
        </div>
      </div>
      }
      @if (type === 'variant' && variantLoading) {
      <div class="rounded-xl bg-gray-100 p-6 text-center text-xs text-gray-500">Loading crate details…</div>
      } @else if (type === 'variant' && variantError) {
      <div class="rounded-xl bg-red-50 border border-red-200 p-3 text-xs text-red-700">{{ variantError }} <button class="underline font-semibold" (click)="loadVariantLabels()">Try again</button></div>
      } @else if (rows.length) {
      <div>
        <div class="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">Preview</div>
        <div class="rounded-xl bg-gray-100 p-4 flex items-center justify-center overflow-hidden">
          <div [innerHTML]="preview"></div>
        </div>
      </div>
      }
    </div>
  </div>
</mat-dialog-content>

<div class="px-6 py-4 border-t border-gray-100 bg-white flex flex-row items-center justify-between gap-3">
  <span class="text-xs text-gray-400">{{ totalLabels }} label{{ totalLabels === 1 ? '' : 's' }} &middot; {{ type === 'variant' ? variantSize.label + ', one per sheet' : layout === 'roll' ? 'one per page' : 'on A4' }}</span>
  <div class="flex items-center gap-3">
    <button type="button" (click)="dialogRef.close()" class="px-5 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-700 text-sm font-semibold hover:bg-gray-50">Cancel</button>
    <button type="button" [disabled]="!totalLabels || (type === 'variant' && variantLoading)" (click)="print()"
      class="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-gradient-to-b from-amber-400 to-amber-500 text-neutral-900 font-bold text-sm shadow-md shadow-amber-200 hover:from-amber-300 hover:to-amber-400 transition-all disabled:opacity-50 disabled:shadow-none">
      <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">print</mat-icon> Print {{ totalLabels }} label{{ totalLabels === 1 ? '' : 's' }}
    </button>
  </div>
</div>
